// The customers' multi-select list of Admin → Customer assignments — Add's Customer list and a row's
// "+ Link" alike: a tick per row (customers sharing a shown name one row, every code under it), the list
// staying open and its search box keeping the focus, ticks kept across searches; "Select all N shown ·
// Clear" on top, "Hide no open lines" at the bottom. A code the row or person has already says so and
// can't be ticked; one someone else links says "also Kai (Owner)" and can be. The rules are
// `assignments.ts`'s (`pickOffer`, `visiblePicks`, `togglePick`, `selectAllShown`).

import { useState } from "react";
import type { KeyboardEvent } from "react";
import { XIcon } from "lucide-react";
import { alsoLinkedText, countText, holdersHover, otherHolders, pickOffer } from "@app/shared";
import type { CodeHolder, CustomerPick } from "@app/shared";
import { cn } from "@/lib/utils";
import { SourceBadge } from "./source-filter";

/** A row of the list: the pick as it can be ticked, and what it says beside it. */
export interface PickRow {
  pick: CustomerPick;
  /** Every code linked already (this row's, or this person's): not tickable. */
  already: boolean;
  /** The row's own suggestion ("+ Link"): marked "suggested". */
  suggested: boolean;
  /** Someone else's assignments linking its codes. */
  others: CodeHolder[];
}

/** The picks as rows: what is linked already (`mine`) taken off each, who else links them. */
export function pickRows(
  picks: readonly CustomerPick[],
  o: {
    mine: ReadonlySet<string>;
    holders: ReadonlyMap<string, readonly CodeHolder[]>;
    personEmail: string;
    suggested?: ReadonlySet<string>;
  },
): PickRow[] {
  return picks.map((p) => {
    const offer = pickOffer(p, o.mine);
    const others = [
      ...new Map(
        p.codes
          .flatMap((c) => otherHolders(o.holders, c.key, { personEmail: o.personEmail }))
          .map((h) => [`${h.personEmail}|${h.role}`, h]),
      ).values(),
    ];
    return { pick: offer.pick, already: offer.already, suggested: !!o.suggested?.has(p.id), others };
  });
}

/**
 * The list's keys, on its search box: ↑/↓ move, Space ticks the row reached with them (typed, a space),
 * Enter does `onEnter` (Add: tick; "+ Link": link), Esc `onEscape`.
 */
export function usePickNav(
  rows: readonly PickRow[],
  o: { onTick: (r: PickRow) => void; onEnter?: () => void; onEscape: () => void },
) {
  const [at, setAt] = useState(0);
  const [moved, setMoved] = useState(false);
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      o.onEscape();
      return;
    }
    if (e.key === "Enter" && o.onEnter) {
      e.preventDefault();
      o.onEnter();
      return;
    }
    if (!rows.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setMoved(true);
      setAt((i) => (i + (e.key === "ArrowDown" ? 1 : -1) + rows.length) % rows.length);
    } else if (e.key === "Enter" || (e.key === " " && moved)) {
      e.preventDefault();
      o.onTick(rows[Math.min(at, rows.length - 1)]!);
    }
  };
  /** What is typed changed: back to the first row, Space types again. */
  const reset = () => {
    setAt(0);
    setMoved(false);
  };
  return { at, setAt, onKeyDown, reset };
}

/**
 * A row: wraps, so its tags go under the name when the list is narrow; the name and code never shrink
 * below 180 px (`truncate` past that, the full text on hover).
 */
export const PICK_ROW = "flex flex-wrap items-center gap-x-1.5 gap-y-0.5";
export const PICK_NAME = "min-w-[180px] flex-1 truncate";
/** The list's height: at most 16rem, and 40% of the window — on a short one the form's buttons stay in view. */
export const PICK_LIST_HEIGHT = "max-h-[min(16rem,40dvh)]";

/** A row's code beside its name: "3 codes" for a group, the code, or "code · original name". */
const pickCodeText = (p: CustomerPick) =>
  p.codes.length > 1
    ? countText(p.codes.length, "code")
    : p.by === "shown"
      ? p.codes[0]!.code
      : `${p.codes[0]!.code} · ${p.codes[0]!.name}`;

/** A row's hover: everything it says, in full — "BELK · BLK · suggested · also Dijana (TEST) (Owner) · 10 lines". */
export const pickRowTitle = (r: PickRow, alreadyText: string) =>
  [
    r.pick.label,
    pickCodeText(r.pick),
    r.suggested ? "suggested" : "",
    r.already ? alreadyText : r.others.length ? holdersHover(r.others).replaceAll("\n", "; ") : "",
    r.pick.openLines ? countText(r.pick.openLines, "line") : "no open lines",
  ]
    .filter(Boolean)
    .join(" · ");

export function PickList({
  rows,
  ticked,
  at,
  onAt,
  onToggle,
  onSelectAll,
  onClear,
  hideNoOpen,
  onHideNoOpen,
  alreadyText,
  className,
  testId,
}: {
  rows: readonly PickRow[];
  ticked: readonly CustomerPick[];
  at: number;
  onAt: (i: number) => void;
  onToggle: (r: PickRow) => void;
  onSelectAll: () => void;
  onClear: () => void;
  hideNoOpen: boolean;
  onHideNoOpen: (on: boolean) => void;
  /** "already linked", "already Shai's". */
  alreadyText: string;
  className?: string;
  testId?: string;
}) {
  const tickable = rows.filter((r) => !r.already).length;
  const isTicked = (id: string) => ticked.some((p) => p.id === id);
  return (
    // Clicks inside keep the focus in the search box: the list stays open.
    <div
      className={cn("bg-background rounded-md border", className)}
      data-testid={testId}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="flex items-center gap-2 border-b px-2 py-1 text-xs">
        <button
          type="button"
          className="text-primary font-medium disabled:opacity-50"
          disabled={!tickable}
          onClick={onSelectAll}
          data-testid="pick-list-all"
        >
          Select all {tickable.toLocaleString("en-US")} shown
        </button>
        <span className="text-muted-foreground">·</span>
        <button
          type="button"
          className="text-primary font-medium disabled:opacity-50"
          disabled={!ticked.length}
          onClick={onClear}
        >
          Clear
        </button>
      </div>
      <div role="listbox" aria-multiselectable className={cn(PICK_LIST_HEIGHT, "overflow-y-auto p-1")}>
        {rows.length ? null : <p className="text-muted-foreground p-2 text-xs">No customer matches.</p>}
        {rows.map((r, i) => {
          const p = r.pick;
          const on = isTicked(p.id);
          return (
            <div
              key={p.id}
              role="option"
              aria-selected={on}
              aria-disabled={r.already}
              className={cn(
                PICK_ROW,
                "w-full cursor-pointer rounded px-1.5 py-1 text-left",
                i === at && "bg-muted",
                r.already && "text-muted-foreground cursor-default",
              )}
              title={pickRowTitle(r, alreadyText)}
              onMouseEnter={() => onAt(i)}
              onClick={() => onToggle(r)}
            >
              <input type="checkbox" tabIndex={-1} readOnly checked={on || r.already} disabled={r.already} />
              {[...new Set(p.codes.map((c) => c.source))].map((s) => (
                <SourceBadge key={s} source={s} />
              ))}
              {/* The name and code keep their width (PICK_NAME); the tags wrap under them instead. */}
              <span className={PICK_NAME}>
                <span className="font-medium">{p.label}</span>{" "}
                <span className="text-muted-foreground text-xs">{pickCodeText(p)}</span>
              </span>
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                {p.openLines ? countText(p.openLines, "line") : "no open lines"}
              </span>
              {r.suggested || r.already || r.others.length ? (
                <span className="text-muted-foreground flex shrink-0 flex-wrap gap-x-1.5 text-[11px]">
                  {r.suggested ? <span className="text-sky-700">suggested</span> : null}
                  {r.already ? (
                    <span>{alreadyText}</span>
                  ) : r.others.length ? (
                    <span>{alsoLinkedText(r.others)}</span>
                  ) : null}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      <label className="flex items-center gap-1.5 border-t px-2 py-1 text-xs">
        <input type="checkbox" checked={hideNoOpen} onChange={(e) => onHideNoOpen(e.target.checked)} />
        Hide no open lines
      </label>
    </div>
  );
}

/** The ticked customers as chips: badge, code and name (a group: its name and "3 codes"), × each. */
export function PickChips({ picks, onRemove }: { picks: readonly CustomerPick[]; onRemove: (id: string) => void }) {
  if (!picks.length) return null;
  return (
    <div className="space-y-1" data-testid="add-picks">
      <div className="text-muted-foreground text-xs">{countText(picks.length, "customer")}</div>
      <div className="flex flex-wrap gap-1">
        {picks.map((p) => (
          <span
            key={p.id}
            className="bg-muted inline-flex items-center gap-1 rounded border py-0.5 pr-0.5 pl-1.5 text-xs"
            title={p.codes.map((c) => `${c.source} ${c.code} · ${c.name}`).join("\n")}
          >
            {[...new Set(p.codes.map((c) => c.source))].map((s) => (
              <SourceBadge key={s} source={s} />
            ))}
            {p.codes.length > 1 ? (
              <>
                <span className="font-medium">{p.label}</span>
                <span className="text-muted-foreground">{countText(p.codes.length, "code")}</span>
              </>
            ) : (
              <>
                <span className="font-mono">{p.codes[0]!.code}</span>
                <span className="text-muted-foreground max-w-40 truncate">{p.codes[0]!.name || p.label}</span>
              </>
            )}
            <button
              type="button"
              aria-label={`Remove ${p.label}`}
              className="hover:bg-background rounded p-0.5"
              onClick={() => onRemove(p.id)}
            >
              <XIcon className="size-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
